import type { KeyboardEvent } from "react";

export interface TabItem<K extends string> {
  key: K;
  label: string;
  count?: number | undefined;
}

export function Tabs<K extends string>({
  items,
  value,
  onChange,
  label,
}: {
  items: TabItem<K>[];
  value: K;
  onChange: (key: K) => void;
  label: string;
}) {
  // Arrow keys, Home and End move between tabs and select; Tab leaves the list (ARIA tabs pattern).
  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    const at = items.findIndex((i) => i.key === value);
    const step = { ArrowRight: at + 1, ArrowLeft: at - 1, Home: 0, End: items.length - 1 }[e.key];
    if (step === undefined) return;
    e.preventDefault();
    const next = (step + items.length) % items.length;
    const tab = items[next];
    if (!tab) return;
    onChange(tab.key);
    e.currentTarget.querySelectorAll<HTMLElement>('[role="tab"]')[next]?.focus();
  };
  return (
    <div
      role="tablist"
      aria-label={label}
      className="flex gap-0.5 border-b border-line"
      onKeyDown={onKeyDown}
    >
      {items.map((item) => (
        <button
          key={item.key}
          type="button"
          role="tab"
          className="tab"
          aria-selected={item.key === value}
          tabIndex={item.key === value ? 0 : -1}
          onClick={() => onChange(item.key)}
        >
          {item.label}
          {item.count !== undefined && <span className="font-mono text-[11px] text-faint">{item.count}</span>}
        </button>
      ))}
    </div>
  );
}

/** A segmented control: one of a few filters, shown as pressed buttons. */
export function Segmented<K extends string>({
  items,
  value,
  onChange,
  label,
}: {
  items: TabItem<K>[];
  value: K;
  onChange: (key: K) => void;
  label: string;
}) {
  return (
    <div className="seg" role="group" aria-label={label}>
      {items.map((item) => (
        <button
          key={item.key}
          type="button"
          aria-pressed={item.key === value}
          onClick={() => onChange(item.key)}
        >
          {item.label}
          {item.count !== undefined && <span className="font-mono text-[11px] text-faint">{item.count}</span>}
        </button>
      ))}
    </div>
  );
}
